import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { createPortal } from 'react-dom';
import { Transition } from 'react-transition-group';

import noop from 'lodash/noop';

import { subComponentsShape, contentOptionsShape, CONTENT_SIZE } from './Content/Content';
import Layout from './Layout/Layout';
import { CLOSING_REASON } from './constants';
import * as helpers from './helpers';
import ModalCheckIcon from './icons/ModalCheckIcon/ModalCheckIcon';
import ModalErrorIcon from './icons/ModalErrorIcon/ModalErrorIcon';
import ModalMessageIcon from './icons/ModalMessageIcon/ModalMessageIcon';
import ModalSearchErrorIcon from './icons/ModalSearchErrorIcon/ModalSearchErrorIcon';
import ModalSystemErrorIcon from './icons/ModalSystemErrorIcon/ModalSystemErrorIcon';
import ModalWarningIcon from './icons/ModalWarningIcon/ModalWarningIcon';

const Portal = ({ children }) => {
  if (typeof window === 'undefined') {
    return null;
  }

  return createPortal(children, document.body);
};

class Modal extends PureComponent {
  static propTypes = {
    isOpen: PropTypes.bool,

    children: PropTypes.oneOfType([
      /** Рекомендуемый способ использования для типовых попапов (см. Content) */
      PropTypes.shape(subComponentsShape),
      PropTypes.node,
      PropTypes.func,
    ]).isRequired,

    /** Блокирует закрытие при клике по оверлею, прячет крестик, не реагирует на Esc */
    preventUserClose: PropTypes.bool,
    closeOnOutsideClick: PropTypes.bool,
    closeOnEsc: PropTypes.bool,

    LayoutComponent: PropTypes.func,
    PortalComponent: PropTypes.func,

    overlayClassName: PropTypes.string,
    layoutContainerClassName: PropTypes.string,

    /** Исторически сложившийся способ управлять контентом модала */
    contentOptions: PropTypes.shape(contentOptionsShape),

    /** Стили для лэйаута. Используется в WL */
    forcedLayoutStyles: PropTypes.object,

    /** Автоматически включает фулскрин режим,
     * если ширина вьюпорта <= fullScreenBreakpoint */
    fullScreenOnMobile: PropTypes.bool,
    bottomSheetOnMobile: PropTypes.bool,
    fullScreenBreakpoint: PropTypes.number,

    /** Элемент, при клике по которому вызывается модал.
     * В этом случае не требуется isOpen, модалка управялет своим стейтом сама */
    trigger: PropTypes.node,

    /** Дополнительный слой, куда можно пробросить, например, управляющие элементы (left, right) */
    additionalLayer: PropTypes.node,

    /** локатор для тестирования */
    specificLocators: PropTypes.string,

    onClose: PropTypes.func,

    /** Свой ref для получения доступа к контейнеру layout. Нужен чтобы детектить скролл в модалке */
    layoutRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  };

  static defaultProps = {
    isOpen: false,

    PortalComponent: Portal,
    LayoutComponent: Layout,

    contentOptions: {},

    preventUserClose: false,
    closeOnOutsideClick: true,
    closeOnEsc: true,

    fullScreenOnMobile: false,
    bottomSheetOnMobile: false,
    // согласовано с дизом, соответствует адаптивным брейкпоинтам.
    fullScreenBreakpoint: 767,

    onClose: noop,

    layoutRef: null,
  };

  constructor(props, ctx) {
    super(props, ctx);

    this.state = {
      isFullScreen: this.isFullScreen(),
      isBottomSheet: this.isBottomSheet(),
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('keydown', this.handleKeyDown);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    if (
      prevProps.fullScreenOnMobile !== this.props.fullScreenOnMobile ||
      prevProps.bottomSheetOnMobile !== this.props.bottomSheetOnMobile ||
      prevProps.fullScreenBreakpoint !== this.props.fullScreenBreakpoint
    ) {
      this.setState({
        isFullScreen: this.isFullScreen(),
        isBottomSheet: this.isBottomSheet(),
      });
    }
  }

  isFullScreen() {
    const { fullScreenOnMobile, fullScreenBreakpoint, bottomSheetOnMobile } = this.props;

    const isBottomSheetLandscapeMode =
      bottomSheetOnMobile && window.orientation !== 'undefined' && window.orientation !== 0;

    if ((fullScreenOnMobile && !bottomSheetOnMobile) || isBottomSheetLandscapeMode) {
      const viewportWidth = window.innerWidth;

      return viewportWidth <= fullScreenBreakpoint;
    }

    return false;
  }

  isBottomSheet() {
    const { bottomSheetOnMobile, fullScreenBreakpoint } = this.props;

    if (bottomSheetOnMobile && window.orientation === 0) {
      const viewportWidth = window.innerWidth;

      return viewportWidth <= fullScreenBreakpoint;
    }

    return false;
  }

  handleResize = () => {
    this.setState({
      isFullScreen: this.isFullScreen(),
      isBottomSheet: this.isBottomSheet(),
    });
  };

  handleKeyDown = (e) => {
    const { preventUserClose, closeOnEsc } = this.props;

    if (preventUserClose || !closeOnEsc) {
      return null;
    }

    if (e.keyCode === 27) {
      this.closeModal(true, CLOSING_REASON.ESCAPE_KEY);
    }
  };

  handleTriggerClick = (cb = noop, event) => {
    this.setState({
      isOpenByTrigger: true,
    });

    cb(event);
  };

  closeModal = (isClosedByUser, reason) => {
    const { isOpenByTrigger } = this.state;
    const { onClose, isOpen } = this.props;

    if (isOpen || isOpenByTrigger) {
      onClose(isClosedByUser, reason);

      this.setState({
        isOpenByTrigger: false,
      });
    }
  };

  render() {
    const { isOpenByTrigger, isFullScreen, isBottomSheet } = this.state;

    const {
      isOpen,
      children,

      PortalComponent,
      LayoutComponent,

      className,

      overlayClassName,
      layoutContainerClassName,
      specificLocators,
      forcedLayoutStyles,

      preventUserClose,
      closeOnOutsideClick,

      contentOptions,

      trigger,

      additionalLayer,
      layoutRef,
    } = this.props;

    if (!children) {
      return null;
    }

    const isModalOpen = isOpen || isOpenByTrigger;

    const modalInPortal = (
      <Transition
        in={isModalOpen}
        appear={isModalOpen}
        timeout={300}
        mountOnEnter={true}
        unmountOnExit={true}
      >
        <PortalComponent>
          <>
            <LayoutComponent
              layoutRef={layoutRef}
              isOpen={isModalOpen}
              isFullScreen={isFullScreen}
              isBottomSheet={isBottomSheet}
              closeModal={this.closeModal}
              className={className}
              forcedLayoutStyles={forcedLayoutStyles}
              overlayClassName={overlayClassName}
              layoutContainerClassName={layoutContainerClassName}
              closeOnOutsideClick={preventUserClose ? false : closeOnOutsideClick}
              contentOptions={{
                specificLocators,
                ...{
                  ...contentOptions,
                  hideClose: preventUserClose ? true : contentOptions.hideClose,
                },
              }}
            >
              {this.props.children}
            </LayoutComponent>

            {additionalLayer}
          </>
        </PortalComponent>
      </Transition>
    );

    // модалка сама управляет своим состоянием
    if (trigger) {
      const triggerWithHandler = React.cloneElement(trigger, {
        onClick: this.handleTriggerClick.bind(this, trigger?.props?.onClick),
      });

      return (
        <span>
          {triggerWithHandler}
          {modalInPortal}
        </span>
      );
    }

    // родитель определяет состояние модалки
    return modalInPortal;
  }
}
let Constructor = Modal;
try {
  if (__WHITE_LABEL__) {
    // Временное решение, исправится в задаче https://onetwotripdev.atlassian.net/browse/FC-498
    let WLModal = require('@ott/white-label/modal');
    if (WLModal.default) {
      WLModal = WLModal.default;
    }
    Constructor = function (prop) {
      return <WLModal {...prop} modalComponent={Modal} />;
    };
    Constructor.propTypes = Modal.propTypes;
  }
} catch (e) {}

export default Constructor;

export { CLOSING_REASON, CONTENT_SIZE, helpers };

export const modalIcons = {
  ModalErrorIcon,
  ModalWarningIcon,
  ModalSearchErrorIcon,
  ModalSystemErrorIcon,
  ModalCheckIcon,
  ModalMessageIcon,
};
